import { ref, onMounted, computed } from 'vue';
import { useTraceTail } from './useTraceTail';
import { Fingerprint, FingerprintComposable } from '../types';

export function useFingerprint(): FingerprintComposable {
  const { client, options } = useTraceTail();
  
  const fingerprint = ref<Fingerprint | null>(null);
  const loading = ref(true);
  const error = ref<Error | null>(null);

  // Computed properties for easy access
  const visitorId = computed(() => fingerprint.value?.visitorId || null);
  const confidence = computed(() => fingerprint.value?.confidence || 0);
  const riskScore = computed(() => fingerprint.value?.riskScore || 0);
  const fraudulent = computed(() => fingerprint.value?.fraudulent || false);
  const signals = computed(() => fingerprint.value?.signals || null);

  const loadFingerprint = async () => {
    try {
      loading.value = true;
      error.value = null;

      // Handle test mode
      if (options.config?.testMode && options.config.mockData) {
        fingerprint.value = {
          visitorId: 'mock-visitor-123',
          confidence: 0.99,
          riskScore: 0.1,
          fraudulent: false,
          signals: {} as any,
          timestamp: new Date(),
          ...options.config.mockData
        } as Fingerprint;
        loading.value = false;
        return;
      }

      const result = await client.generateFingerprint();
      
      fingerprint.value = {
        visitorId: result.visitorId,
        confidence: result.confidence,
        riskScore: 0,  // Not in base SDK
        fraudulent: false,  // Not in base SDK
        signals: result.components || {} as any,
        timestamp: new Date()
      };
    } catch (err) {
      error.value = err as Error;
      console.error('[TraceTail] Error getting fingerprint:', err);
    } finally {
      loading.value = false;
    }
  };

  const retry = async () => {
    await loadFingerprint();
  };

  const refresh = async () => {
    fingerprint.value = null;
    await loadFingerprint();
  };

  // Load fingerprint on mount
  onMounted(() => {
    loadFingerprint();
  });

  return {
    visitorId,
    confidence,
    riskScore,
    fraudulent,
    signals,
    loading,
    error,
    retry,
    refresh
  };
}